import React from 'react'
import {
  Card,
  CardHeader,
  Divider,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material'
import { PropertyList } from '../../../../../components/PropertyList'
import { PropertyListItem } from '../../../../../components/PropertyListItem'
import CircularLoading from '../../../../../components/CircularProgress'
import {
  EOrderStatus,
  EOrderType,
  IOrderItem,
} from '../../../../../types/order'
import OrderTag from '../../../../../components/OrderTag'
import dayjs from 'dayjs'
import { DATE_FORMAT, ONLY_DATE_FORMAT } from '../../../../../constants/global'
import { ERecipientPhoneRule, IMerchant } from '../../../../../types/merchant'
import { RECIPIENT_PHONE_RULE_OPTIONS } from '../../../../../constants/merchant'
import { normalizeRecipientPhoneRule } from '../../../../../utils/recipient-phone-rule.util'

export interface IOderDetailCard {
  status?: EOrderStatus
  quantity?: number
  commission?: number
  paymentMethod?: EOrderType
  items?: IOrderItem[]
  actor?: string
  distribution?: string
  createdAt?: string
  updatedAt?: string
  note?: string
}

interface FilterGroupProps {
  isLoading: boolean
  merchant?: IMerchant
  updateMerchant?: (data: Partial<IMerchant>) => void
}

const MerchantSetUpProductsAndOrdersCard = ({
  isLoading,
  merchant,
  updateMerchant,
}: FilterGroupProps) => {
  const selectedRecipientPhoneRule = normalizeRecipientPhoneRule(
    merchant?.config?.recipientPhoneRule,
  )

  return (
    <>
      <CircularLoading isLoading={isLoading} />
      <Card
        sx={{
          padding: 2,
        }}
      >
        <CardHeader title="ตั้งค่าสินค้าและการสั่งซื้อ" />
        <Divider />
        <PropertyList>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <TextField
                id="comission"
                fullWidth
                label="ค่าคอมมิสชั่น"
                name="comission"
                defaultValue={merchant?.config.comission}
                onChange={(e) =>
                  updateMerchant?.({
                    config: {
                      ...merchant?.config,
                      comission: Math.max(0, Number(e.target.value) || 0),
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="shippingCostFix"
                fullWidth
                label="ค่าส่ง"
                name="shippingCostFix"
                defaultValue={merchant?.config.shippingCostFix}
                onChange={(e) =>
                  updateMerchant?.({
                    config: {
                      ...merchant?.config,
                      shippingCostFix: Math.max(0, Number(e.target.value) || 0),
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="codPercent"
                fullWidth
                label="ค่า COD"
                name="codPercent"
                defaultValue={merchant?.config.codPercent}
                onChange={(e) =>
                  updateMerchant?.({
                    config: {
                      ...merchant?.config,
                      codPercent: Math.max(0, Number(e.target.value) || 0),
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="returnPercent"
                fullWidth
                label="การตีกลับ"
                name="returnPercent"
                defaultValue={merchant?.config.returnPercent}
                onChange={(e) =>
                  updateMerchant?.({
                    config: {
                      ...merchant?.config,
                      returnPercent: Math.max(0, Number(e.target.value) || 0),
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="commissionPercent"
                fullWidth
                label="ค่าคอม %"
                name="commissionPercent"
                defaultValue={merchant?.config.commissionPercent}
                onChange={(e) =>
                  updateMerchant?.({
                    config: {
                      ...merchant?.config,
                      commissionPercent: Math.max(
                        0,
                        Number(e.target.value) || 0,
                      ),
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="commissionOrder"
                fullWidth
                label="ค่าคอมต่อคำสั่งซื้อ"
                name="commissionOrder"
                defaultValue={merchant?.config.commissionOrder}
                onChange={(e) =>
                  updateMerchant?.({
                    config: {
                      ...merchant?.config,
                      commissionOrder: Math.max(0, Number(e.target.value) || 0),
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="taxPercent"
                fullWidth
                label="ภาษี"
                name="taxPercent"
                defaultValue={merchant?.config.taxPercent}
                onChange={(e) =>
                  updateMerchant?.({
                    config: {
                      ...merchant?.config,
                      taxPercent: Math.max(0, Number(e.target.value) || 0),
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="dulplicateOrderDate"
                fullWidth
                label="จำนวนวันที่ของคำสั่งซื้อซ้ำ"
                name="dulplicateOrderDate"
                defaultValue={merchant?.config.dulplicateOrderDate || 1}
                onChange={(e) =>
                  updateMerchant?.({
                    config: {
                      ...merchant?.config,
                      dulplicateOrderDate: Math.max(
                        0,
                        Number(e.target.value) || 0,
                      ),
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="recipientPhoneRule"
                select
                fullWidth
                label="กฎเบอร์ผู้รับ"
                name="recipientPhoneRule"
                value={selectedRecipientPhoneRule}
                helperText={
                  RECIPIENT_PHONE_RULE_OPTIONS.find(
                    (option) => option.value === selectedRecipientPhoneRule,
                  )?.description
                }
                onChange={(e) =>
                  updateMerchant?.({
                    config: {
                      ...merchant?.config,
                      recipientPhoneRule: e.target.value as ERecipientPhoneRule,
                    },
                  })
                }
              >
                {RECIPIENT_PHONE_RULE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </PropertyList>
      </Card>
    </>
  )
}

export default MerchantSetUpProductsAndOrdersCard
